import { useEffect, useRef, useState } from "react";
import { Loader2, Move, RotateCcw, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const F = 260;
const OUT = 512;

const useImage = (file) => {
  const [img, setImg] = useState(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return img;
};

export const PhotoCropDialog = ({ file, round, onCancel, onDone }) => {
  const img = useImage(file);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const base = img ? F / Math.min(img.width, img.height) : 1;
  const w = img ? img.width * base * zoom : F;
  const h = img ? img.height * base * zoom : F;
  const clamp = (o) => ({ x: Math.max(-(w - F) / 2, Math.min((w - F) / 2, o.x)), y: Math.max(-(h - F) / 2, Math.min((h - F) / 2, o.y)) });
  useEffect(() => { setOff((o) => clamp(o)); }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps
  const left = F / 2 - w / 2 + off.x;
  const top = F / 2 - h / 2 + off.y;
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, o: off }; };
  const move = (e) => { if (drag.current) setOff(clamp({ x: drag.current.o.x + e.clientX - drag.current.sx, y: drag.current.o.y + e.clientY - drag.current.sy })); };
  const save = () => {
    setBusy(true);
    const c = document.createElement("canvas");
    c.width = OUT;
    c.height = OUT;
    const k = OUT / F;
    c.getContext("2d").drawImage(img, left * k, top * k, w * k, h * k);
    c.toBlob((b) => onDone(new File([b], "photo.jpg", { type: "image/jpeg" })), "image/jpeg", 0.9);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent data-testid="crop-dialog" className="max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-2xl">Position photo</DialogTitle></DialogHeader>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Move className="h-3.5 w-3.5" /> Drag to centre, use the slider to zoom.</p>
        <div className="flex justify-center">
          <div data-testid="crop-frame" onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }}
            onWheel={(e) => setZoom((z) => Math.min(4, Math.max(1, z - e.deltaY / 500)))}
            className={`relative cursor-grab touch-none select-none overflow-hidden bg-black ring-4 ring-primary active:cursor-grabbing ${round ? "rounded-full" : "rounded-2xl"}`} style={{ width: F, height: F }}>
            {img ? <img src={img.src} alt="" draggable={false} className="pointer-events-none absolute max-w-none" style={{ left, top, width: w, height: h }} />
              : <Loader2 className="absolute inset-0 m-auto h-6 w-6 animate-spin text-primary" />}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ZoomIn className="h-4 w-4 text-muted-foreground" />
          <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom" className="w-full accent-[#FEDC19]" />
          <button type="button" onClick={() => { setZoom(1); setOff({ x: 0, y: 0 }); }} data-testid="crop-reset" aria-label="Reset" className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary"><RotateCcw className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="crop-cancel" className="rounded-full border py-2.5 text-sm font-semibold">Cancel</button>
          <button type="button" onClick={save} disabled={!img || busy} data-testid="crop-save" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save photo
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
